import { Component, effect, inject, OnInit } from '@angular/core';
import { TypeDropdownComponent } from '../../atoms/type-dropdown/type-dropdown.component';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { DataLayerType } from '../../../models/data-layer-type';
import { DataLayer } from '../../../models/data-layer';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import {
  CustomGridStackWidget,
  GridWidgetConfigService,
  PoiConf,
  WegweiserRegion,
} from '../../../services/grid-widget-config.service';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DropdownModule } from 'primeng/dropdown';
import { Indicator } from '../../../models/indicator';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { SelectButtonModule } from 'primeng/selectbutton';
import { MultiSelectModule } from 'primeng/multiselect';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { SvgIconComponent } from '../../atoms/svg-icon/svg-icon.component';
import { TypeMultiSelectComponent } from '../../atoms/type-multi-select/type-multi-select.component';
import { GeoGroupDropdownComponent } from '../../atoms/geo-group-dropdown/geo-group-dropdown.component';
import { WegweiserStore } from '../../../stores/wegweiser-api/wegweiser.state';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { NgClass } from '@angular/common';
import { ChipsModule } from 'primeng/chips';
import { DebounceInputDirective } from '../../../directives/debounce-input.directive';
import { getState } from '@ngrx/signals';
import { ButtonModule } from 'primeng/button';
import { HeaderComponent } from '../../atoms/header/header.component';
import { DividerModule } from 'primeng/divider';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TooltipModule } from 'primeng/tooltip';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { ContextSwitchComponent } from '../../atoms/context-switch/context-switch.component';
import { PoiListComponent } from '../poi-list/poi-list.component';
import { PoiTypeItemComponent } from '../poi-type-item/poi-type-item.component';

@Component({
  selector: 'app-widget-data-layer',
  standalone: true,
  imports: [
    TypeDropdownComponent,
    CheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    InputSwitchModule,
    DropdownModule,
    SelectButtonModule,
    MultiSelectModule,
    CdkDrag,
    CdkDropList,
    NgClass,
    SvgIconComponent,
    TypeMultiSelectComponent,
    GeoGroupDropdownComponent,
    FormFieldWrapperComponent,
    ChipsModule,
    DebounceInputDirective,
    TooltipModule,
    ButtonModule,
    HeaderComponent,
    DividerModule,
    ScrollPanelModule,
    ContextSwitchComponent,
    PoiListComponent,
    PoiTypeItemComponent,
  ],
  templateUrl: './widget-data-layer.component.html',
  styleUrl: './widget-data-layer.component.scss',
})
export class WidgetDataLayerComponent implements OnInit {
  readonly widgetStore = inject(WidgetStore);
  readonly globalStore = inject(GlobalsStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly layerStore = inject(DataLayerStore);
  readonly typeStore = inject(DataLayerTypeStore);
  readonly wegweiserStore = inject(WegweiserStore);

  regionSearchString = '';
  regionResult: WegweiserRegion[] = [];
  activeWidget: CustomGridStackWidget | null = null;
  public selectedDataLayerType: DataLayerType | null = null;
  public selectedDataLayer: DataLayer | null = null;

  availableConfig: string[] = [];
  poiToShow: DataLayerType[] = [];
  poiConf: PoiConf[] = [];
  poiGroupConf: Record<string, string | null> = {};
  expandedPois = new Set<string>();

  constructor(private configService: GridWidgetConfigService) {
    effect(() => {
      const wegweiserState = getState(this.wegweiserStore);
      this.activeWidget = this.widgetStore.selected();
    });
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * Used here to set the current active widget, initialize context options, and perform additional setup operations.
   *
   * @return {void} Does not return a value.
   */
  ngOnInit(): void {
    this.activeWidget = this.widgetStore.selected();
    this.initialize();
  }

  /**
   * Initializes the active widget by setting the selected data layer, data layer type, and available configurations.
   * If the widget's context is external, the method returns immediately.
   * Updates chart data if a selected data layer is found and initializes points of interest (POI).
   * If no data layer is provided but polygon layers exist, it sets the selected data layer type to the first polygon layer.
   *
   * @return {void} This method does not return a value.
   */
  initialize(): void {
    if (this.activeWidget && this.typeStore.asArray().length === 0) {
      this.activeWidget.widget.context = 'external';
    }

    if (
      !this.activeWidget?.widget ||
      this.activeWidget?.widget?.context === 'external'
    )
      return;

    if (this.activeWidget.widget.dataLayer) {
      this.selectedDataLayer = this.layerStore.getById(
        this.activeWidget?.widget.dataLayer
      );

      this.selectedDataLayerType = this.typeStore.getById(
        this.activeWidget.widget.dataLayerType
      );
    }

    if (!this.selectedDataLayerType) {
      const polygonLayers = this.typeStore.polygonLayers();
      this.selectedDataLayerType = polygonLayers.at(0) || null;
      if (this.selectedDataLayerType)
        this.setDataLayerType(this.selectedDataLayerType, false);
    }

    this.availableConfig = this.configService.getDataLayerFieldsByType(
      this.activeWidget.type
    );

    if (!this.activeWidget?.widget.dataLayer && this.selectedDataLayer) {
      this.updateChartData(this.selectedDataLayer?._id);
    }

    this.initPoi();

    return;
  }

  /**
   * Sets the data layer type and updates related properties and chart data.
   *
   * @param {DataLayerType} dataLayerType - The data layer type object that is being set.
   * @param {boolean} rerender - rerender chart
   * @return {void}
   */
  setDataLayerType(dataLayerType: DataLayerType, rerender = true): void {
    let idxModifier = 0;

    const latestDataLayer = this.layerStore.getLatestByParent(
      dataLayerType._id,
      idxModifier
    );

    let layer = { ...latestDataLayer };
    while (
      layer &&
      (layer.isForecast || !this.dataStore.dataLayerHasData(layer?._id))
    ) {
      idxModifier++;
      layer = this.layerStore.getLatestByParent(dataLayerType._id, idxModifier);
    }

    this.selectedDataLayer = layer || latestDataLayer;
    const forecastYears = this.layerStore
      .filterByParent(dataLayerType._id)
      .filter(l => l.isForecast)
      .map(l => l.year);

    this.widgetStore.setWidgetProperties({
      dataLayerType: dataLayerType._id,
      dataLayer: this.selectedDataLayer._id,
      year: String(this.selectedDataLayer.year),
      disabledYears: forecastYears,
    });

    if (rerender) {
      this.updateChartData(this.selectedDataLayer._id);
    }
  }

  /**
   * Updates the chart data by optionally updating districts in the widget store and then refreshing the chart data.
   *
   * @param {string} [dataLayerId] - An optional identifier for the data layer. If provided, the widget's districts will be updated in the widget store using this identifier.
   * @return {void} This method does not return any value.
   */
  updateChartData(dataLayerId?: string): void {
    if (dataLayerId && this.activeWidget) {
      this.widgetStore.updateDistricts(this.activeWidget.id, dataLayerId);
    }
    setTimeout(() => {
      this.widgetStore.updateChartData();
    }, 200);
  }

  /**
   * Removes a Point of Interest (POI) from the selection.
   *
   * @param {DataLayerType} poi - The POI to remove.
   */
  removePoi(poi: DataLayerType): void {
    this.poiToShow = this.poiToShow.filter(p => p._id !== poi._id);
    this.savePoiSelectionToConfig(this.poiToShow);
  }

  onDrop($event: CdkDragDrop<any, any>): void {
    console.log('drop event--->', $event);
  }

  /**
   * Saves the selected Points of Interest (POI) to the configuration.
   * Maps the POI data to a configuration object and updates the configuration.
   *
   * @param {DataLayerType[] | undefined} poi - Array of POI objects to be saved into the configuration.
   * If undefined or empty, the configuration will not include any POI data.
   * @return {void} This method does not return a value.
   */
  savePoiSelectionToConfig(poi: DataLayerType[] | undefined): void {
    // Preserve existing configurations
    const existingConfMap = new Map(
      this.poiConf.map(conf => [conf.type, conf])
    );

    this.poiConf = [];
    if (poi && poi.length) {
      poi.forEach(item => {
        // Use existing configuration if available, otherwise create new one
        const existingConf = existingConfMap.get(item._id);
        this.poiConf.push(existingConf || { type: item._id });
      });
    }

    this.updatePoiToConfig();
  }

  /**
   * Retrieves geographical data for a point of interest (POI) for a specific year.
   *
   * @param {number|string} year - The year for which the geographical data is requested. Can be provided as a number or a string.
   * @param {string} poi - The point of interest for which the geographical data is requested.
   * @return {string} The geographical data for the specified POI and year. Returns an empty string if no active widget or widget data is available.
   */
  getPoiGeoDataByYear(year: number | string, poi: string): string {
    if (!this.activeWidget || !this.activeWidget.widget) return '';
    return (
      this.layerStore.filterByParentAndYear(poi, Number(year))?.geoData || ''
    );
  }

  /**
   * Retrieves the data layer for a specific Point of Interest (POI) based on the active widget's year.
   *
   * @param {string} poiId - The unique identifier for the POI.
   * @return {DataLayer | null} The data layer associated with the POI for the current year, or null if not found.
   */
  getPoiLayer(poiId: string): DataLayer | null {
    if (!this.activeWidget?.widget) return null;
    return (
      this.layerStore.filterByParentAndYear(
        poiId,
        Number(this.activeWidget.widget.year)
      ) || null
    );
  }

  /**
   * Handles the event when a Point of Interest (POI) is clicked in the list.
   * Triggers a 'showPoi' event in the widget store to highlight or center the POI on the map.
   *
   * @param {string} key - The unique identifier of the clicked POI.
   * @return {void}
   */
  onPoiClick(key: string): void {
    this.widgetStore.triggerEvent('showPoi', { key });
  }

  togglePoi(id: string): void {
    if (this.expandedPois.has(id)) {
      this.expandedPois.delete(id);
    } else {
      this.expandedPois.add(id);
    }
  }

  isPoiExpanded(id: string): boolean {
    return this.expandedPois.has(id);
  }

  /**
   * Saves the given Point of Interest (POI) indicator and updates the configuration.
   *
   * @param {Indicator} $event - The indicator event object containing the POI details.
   * @param {string} _id - The unique identifier for the POI configuration to be updated.
   * @return {void} Does not return a value.
   */
  savePoiGroup($event: string, _id: string): void {
    if (!$event) {
      delete this.poiGroupConf[_id];
    } else {
      this.poiGroupConf[_id] = $event;
    }
    this.updatePoiToConfig();
  }

  /**
   * Updates the Points of Interest (POI) configuration in the active widget's configuration
   * and persists the changes to the widget store. If there are no POIs in the configuration,
   * the POI field in the widget's map configuration is set to undefined. Finally, updates
   * the chart data associated with the active widget.
   *
   * @return {void} No return value.
   */
  updatePoiToConfig(): void {
    if (!this.activeWidget || !this.activeWidget.widget.map) return;
    this.activeWidget.widget.map.poi = this.poiConf.length
      ? this.poiConf
      : undefined;

    this.activeWidget.widget.map.groups = Object.keys(this.poiGroupConf).length
      ? this.poiGroupConf
      : undefined;

    this.widgetStore.updateWidgetConfigById(
      this.activeWidget.id,
      this.activeWidget.widget
    );

    this.updateChartData();
  }

  /**
   * Initializes Points of Interest (POI) configuration for the application by retrieving and processing
   * data from the active widget. Filters and prepares a list of POIs to be displayed based on the configuration.
   *
   * @return {void} Does not return a value.
   */
  private initPoi(): void {
    const poiConfFromWidget = this.activeWidget?.widget?.map?.poi;
    this.poiGroupConf = this.activeWidget?.widget?.map?.groups || {};
    if (!poiConfFromWidget) return;
    this.poiToShow = [];
    this.poiConf = poiConfFromWidget;
    poiConfFromWidget.forEach((poiConf: PoiConf) => {
      const poiType = this.typeStore.getById(poiConf.type);
      if (poiType) this.poiToShow.push(poiType);
    });
  }

  /**
   * Sets the active group for the widget and updates the chart data accordingly.
   *
   * @param {string} $event - The name of the group to set as active.
   * @return {void} This method does not return a value.
   */
  setActiveGroup($event: string): void {
    if (
      !this.activeWidget ||
      !this.selectedDataLayer ||
      this.activeWidget.widget.activeGroup == $event
    )
      return;

    this.widgetStore.setWidgetProperty(
      'activeGroup',
      $event,
      undefined,
      false,
      'centerMap'
    );
    this.updateChartData(this.selectedDataLayer._id);
  }

  /**
   * Searches for regions using the provided search string and updates the region result.
   * If the search string is empty, the result will be cleared.
   * Fetches regions from the wegweiserStore and populates the region result upon successful retrieval.
   *
   * @return {void} No return value.
   */
  searchForWegweiserRegion(): void {
    if (!this.regionSearchString.length) this.regionResult = [];
    this.wegweiserStore.fetchRegions(this.regionSearchString).then(() => {
      const regions = this.wegweiserStore.regions();
      this.regionResult = [...regions];
    });
  }

  /**
   * Saves the active widget configuration by updating the widget store and synchronizing
   * wegweiser data with the widget configuration.
   *
   * Ensures that the current active widget exists before performing the updates.
   *
   * @return {void} Does not return a value.
   */
  saveToWidgetConfig(): void {
    if (!this.activeWidget?.widget) return;
    this.widgetStore.update(this.activeWidget._id, this.activeWidget, true);
    this.wegweiserStore.updateWegweiserDataAndWriteToWidget(this.activeWidget);
  }

  /**
   * Selects a region and updates the corresponding state and widget configuration.
   *
   * @param {WegweiserRegion} region - The region to be selected. Contains information such as the region ID.
   * @return {void} Does not return any value.
   */
  selectRegion(region: WegweiserRegion): void {
    if (!this.activeWidget?.widget) return;
    this.activeWidget?.widget.wegweiserRequestParams.regionIds.push(region.id);

    this.wegweiserStore.cacheRegion(region);

    if (this.activeWidget.type === 'map') {
      this.loadGeoDataToCache(region);
    } else {
      this.saveToWidgetConfig();
    }
  }

  /**
   * Loads geographic data for the specified region into the cache associated with the active widget.
   *
   * @param {WegweiserRegion} region - The region for which geographic data is to be retrieved and cached.
   * @return {void} This method does not return a value.
   */
  loadGeoDataToCache(region: WegweiserRegion): void {
    if (!this.activeWidget) return;

    const widget = this.activeWidget.widget;
    const wegweiserData =
      widget.wegweiserData ?? this.configService.getWegweiserDataTemplate(); // Safeguard with nullish coalescing
    const requestParams = widget.wegweiserRequestParams;

    if (!wegweiserData || !requestParams) return; // Explicit early return for invalid data

    this.wegweiserStore.fetchGeoData(region).then(() => {
      wegweiserData.geoData = this.wegweiserStore.getGeoDataByIds(
        requestParams.regionIds
      );

      widget.wegweiserData = wegweiserData;
      this.saveToWidgetConfig();
    });
  }

  /**
   * Filters the provided list of regions based on the selection criteria derived from the active widget's parameters.
   *
   * @param {WegweiserRegion[]} regions - The array of regions to filter.
   * @return {WegweiserRegion[] | null} An array of selectable regions that are not included in the selected IDs, or null if no selectable regions are found.
   */
  filterBySelection(regions: WegweiserRegion[]): WegweiserRegion[] | null {
    const selectedIds =
      this.activeWidget?.widget?.wegweiserRequestParams?.regionIds || [];
    const selectableRegions = [
      ...new Set(regions.filter(region => !selectedIds.includes(region.id))),
    ];
    return selectableRegions.length ? selectableRegions : null;
  }

  /**
   * Removes the specified region ID from the selected region IDs for the active widget.
   *
   * @param {number} regionId - The ID of the region to be unselected.
   * @return {void} This method does not return a value.
   */
  unselectRegion(regionId: number): void {
    if (!this.activeWidget?.widget) return;
    const selectedIds =
      this.activeWidget.widget.wegweiserRequestParams.regionIds;
    selectedIds.splice(selectedIds.indexOf(regionId), 1);

    this.activeWidget.widget.wegweiserRequestParams.regionIds = selectedIds;

    if (this.activeWidget.widget.wegweiserData?.geoData?.features) {
      this.activeWidget.widget.wegweiserData.geoData.features =
        this.activeWidget.widget.wegweiserData.geoData.features.filter(
          (feature: any) => feature.id !== regionId
        );
    }

    this.saveToWidgetConfig();
  }
}
